import React, { useEffect, useState } from 'react';
import { Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import _, { isEqual } from 'lodash';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormField } from '../../../../global/components/form-element/form-field';
import { BenefitSurveyFormModel } from './benefit-survey-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { PersonalInfoProps } from '../../models/account-info-epi-props';
import { getPersonBenefitSurveyData, updatePersonBenefitSurveyData } from '../../../../global/ducks/person-prc-api';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { FormTypes } from '../../models/form-models';
import { parseLov } from '../../../../global/helpers/form-data-helper';
import { PersonLov } from '../../../../global/models/lov-proc-model';
import { YesNoOptionsFull } from '../../../../global/constants/lists';
import { NumberField, NumberInputTypes } from '../../../../global/components/form-element/number-field';
import { GuidedProcessSupportProps } from '../../../arc/models/arc-epi-props';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { FormGroup } from '../../../../global/components/layout/form-group';

const emptyFormValue: BenefitSurveyFormModel = {
	organization_client_nbr: '',
	organization_guid: '',
	coverage_source: '',
	coverage_level: '',
	percent_employer_pays: 0,
	employer_pays_pension: '',
	other_coverage: '',
};

const other = 'other';

export function BenefitSurvey() {

	const [benefitSurveyData, setBenefitSurveyData] = useState<BenefitSurveyFormModel>(emptyFormValue);
	const [successFormTypes, setSuccessFormTypes] = useState<FormTypes[]>([]);
	const clientGuid = useSelector<TcsmStates, string>(state => state?.personalInfo[DemographicAppKeys.arc]?.currentPerson?.person_guid);
	const institutionGuid = useSelector<TcsmStates, string>(state => state?.institutionState?.institutionGuid);
	const institutionCn = useSelector<TcsmStates, string>(state => state?.institutionState?.institutionClientNumber);
	const settings = useSelector<TcsmStates, PersonalInfoProps>(state => state?.epiProps?.personalInfoSettingState?.epiContent);
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state?.epiProps?.gpsSettingsState?.epiContent);
	const dispatch = useDispatch();
	const lov = useSelector<TcsmStates, PersonLov>(state => state.lovProcState?.personLov);
	const mappedHealthSource = lov?.CoverageSource ? parseLov(lov?.CoverageSource) : [];
	const mappedHealthLevel = lov?.CoverageLevel ? parseLov(lov?.CoverageLevel) : [];
	const error = useApiResponseError(FormTypes.BENEFIT_SURVEY);
	useEffect(() => {
		if (clientGuid && institutionGuid) {
			dispatch(getPersonBenefitSurveyData(clientGuid, institutionGuid, (res) => setBenefitSurveyData(Object.keys(res?.data)?.length > 0 ? res?.data : emptyFormValue)));
		}
	}, [clientGuid]);

	const initialValues = _.cloneDeep(benefitSurveyData);

	const onSubmitHandler = (values: BenefitSurveyFormModel) => {
		
		const hasNoChanges = isEqual(initialValues, values);

		if (hasNoChanges) {
			setSuccessFormTypes([FormTypes.BENEFIT_SURVEY_SUCCESS_NO_CHANGE]);
			return;
		}

		dispatch(apiErrorClear());
		dispatch(updatePersonBenefitSurveyData(
			clientGuid,
			{
				...values,
				percent_employer_pays: Number.parseFloat(values?.percent_employer_pays?.toString()),
				organization_guid: institutionGuid,
				organization_client_nbr: institutionCn,
			},
			(res) => {
				setSuccessFormTypes([FormTypes.BENEFIT_SURVEY_SUCCESS]);
				setBenefitSurveyData(res?.data);
			},
		));
	};

	return (
		<PageContainer>
			<MainContent>


				<Formik
					initialValues={benefitSurveyData}
					onSubmit={(values) => onSubmitHandler(values)}
					initialErrors={error}
					enableReinitialize
				>
					{(formProps: FormikProps<BenefitSurveyFormModel>) => (
						<Form>
							<FormWrapper
								formikProps={formProps}
								submitText={settings.submitText}
								cancelText={settings.cancelText}
								onCancel={() => {
									formProps.resetForm();
									window.scrollTo(0, 0);
								}}
								successfulForms={successFormTypes}
							>
								<PageHeader
									headerText={settings?.benefitSurveyTitle}
									description={settings.benefitSurveyPageDescription}
								/>
								<Hidden mdUp>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsSettings?.benefitsSurveyGpsTitle} contents={gpsSettings?.benefitsSurveyGps} />
									</SideBarContent>
								</Hidden>
								<DividerSection sectionText={settings?.healthQuestionsTitle} >
									<FormGroup>
										<FormField
											name='coverage_source'
											label={settings?.sourceOfHealthCoverageLabel}
											component='input'
											options={mappedHealthSource}
											errors={formProps.errors}
											touched={formProps.touched}
											select
											placeholder={settings?.selectSourcePlaceHolder}
										/>
										{
											formProps?.values?.coverage_source?.toLocaleLowerCase()?.includes(other) &&
											<FormField
												name='other_coverage'
												label={settings?.otherSourceLabel}
												component='input'
												rows={4}
												errors={formProps.errors}
												touched={formProps.touched}
												multiline
											/>

										}
										<FormField
											name='coverage_level'
											label={settings?.levelOfHealthCoverageLabel}
											component='input'
											options={mappedHealthLevel}
											errors={formProps.errors}
											touched={formProps.touched}
											select
											placeholder={settings?.selectLevelPlaceHolder}
										/>
										<NumberField
											name='percent_employer_pays'
											label={settings.percentagePaidLabel}
											type={NumberInputTypes.Percentage}
											formikProps={formProps}
											autoWidth
										/>
									</FormGroup>
								</DividerSection>
								<DividerSection removeDivider sectionText={settings?.pensionCoverageTile}>
									<FormGroup>

										<FormField
											name='employer_pays_pension'
											label={settings?.employerContributionLabel}
											component='radioGroup'
											options={YesNoOptionsFull}
											touched={formProps.touched}
											errors={formProps.errors}
										/>
									</FormGroup>
								</DividerSection>
							</FormWrapper>
						</Form>)}
				</Formik >
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsSettings?.benefitsSurveyGpsTitle} contents={gpsSettings?.benefitsSurveyGps} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}